import {Box, HStack, Show, Spinner, VStack} from 'platform/foundation';

import {useEffect} from 'react';

import {count} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {PriceDistributionChart} from '../../../../components/PriceDistributionChart/PriceDistributionChart';
import {useFilter} from '../../../../hooks/FilterContext';
import {CatalogueType} from '../../../../hooks/useCatalogue';
import {usePriceReportSearchParams} from '../../../../hooks/usePriceReportSearchParams';
import {PriceReportType} from '../../../../types/PriceReportType';
import {featureFlags} from '../../../../utils/featureFlags';
import {isOnStock} from '../../../../utils/isOnStock';
import {isSoldIn30Days} from '../../../../utils/isSoldIn30Days';
import {isSoldIn90Days} from '../../../../utils/isSoldIn90Days';
import {ChartEncodingLegend} from './components/ChartEncodingLegend';
import {ChartLegend} from './components/ChartLegend';
import {ChartToolbar} from './components/ChartToolbar';
import {ChartTypeSwitch} from './components/ChartTypeSwitch';
import {ChartView} from './components/ChartView';
import {MapView} from './components/MapView';
import {SelectedVehicleCard} from './components/SelectedVehicleCard';
import {defaultChartType} from './consts/defaultChartType';
import {ChartToolbarProvider} from './hooks/ChartToolbarProvider';
import {useSelectedVehicleIdsQueryState} from './hooks/useSelectedVehicleIdsQueryState';
import {ExtendedStatisticsCar} from './types/ExtendedStatisticsCar';
import {getChartHighlightEncodingStyle} from './utils/supportsChartToolbarHighlights';

export interface StatisticsChartProps extends TestIdProps {
  priceReport: PriceReportType;
  data: ExtendedStatisticsCar[];
  catalogue: CatalogueType;
  isLoading?: boolean;
}

const CHART_MIN_HEIGHT = 94;

export function StatisticsChart(props: StatisticsChartProps) {
  const {filter} = useFilter();
  const [selectedVehicleIds, setSelectedVehicleIds] = useSelectedVehicleIdsQueryState();
  const {chartVariant, setChartVariant} = usePriceReportSearchParams();

  useEffect(
    function clearStaleSelectedVehicleIds() {
      if (
        isNotNil(selectedVehicleIds) &&
        ![
          ...props.data.map((vehicle) => vehicle.ad_id),
          ...(props.priceReport.neighborAdIds ?? []),
        ].some((ad_id) => selectedVehicleIds.includes(ad_id))
      ) {
        setSelectedVehicleIds(null);
      }
    },
    [filter, props.data, selectedVehicleIds, setSelectedVehicleIds, props.priceReport.neighborAdIds]
  );

  const onStockCount = count(isOnStock, props.data);
  const soldIn30DaysCount = count((car) => isSoldIn30Days(props.priceReport, car), props.data);
  const soldIn90DaysCount = count((car) => isSoldIn90Days(props.priceReport, car), props.data);
  const chartHighlightEncodingStyle = getChartHighlightEncodingStyle(chartVariant);
  const isChartToolbarVisible = isNotNil(chartHighlightEncodingStyle);

  return (
    <ChartToolbarProvider data={props.data}>
      <VStack spacing={4} grow={1} minHeight={0}>
        <VStack spacing={2} shrink={0}>
          <Box paddingLeft={[0, 0, 19, 19]}>
            <HStack justify="space-between" align="center" spacing={4} wrap>
              <ChartLegend
                onStockCount={onStockCount}
                soldIn30DaysCount={soldIn30DaysCount}
                soldIn90DaysCount={soldIn90DaysCount}
              />
              <ChartTypeSwitch
                chartVariant={chartVariant}
                setChartVariant={setChartVariant}
                defaultChartType={defaultChartType}
                data-testid={suffixTestId('chartTypeSwitch', props)}
              />
            </HStack>
          </Box>
          <Show when={isChartToolbarVisible} whenFeatureEnabled={featureFlags.PR_CHART_ICONS_V2}>
            <Box paddingLeft={[0, 0, 19, 19]}>
              <ChartToolbar data={props.data} data-testid={suffixTestId('chartToolbar', props)} />
            </Box>
          </Show>
        </VStack>
        <Box
          minHeight={CHART_MIN_HEIGHT}
          overflow="hidden"
          flexGrow={1}
          position="relative"
          data-testid={suffixTestId('chartWrapper', props)}
        >
          <Show when={chartVariant !== 'mapView' && chartVariant !== 'priceDistribution'}>
            <ChartView
              chartVariant={chartVariant}
              priceReport={props.priceReport}
              data={props.data}
              selectedVehicleIds={selectedVehicleIds}
              setSelectedVehicleIds={setSelectedVehicleIds}
              data-testid={suffixTestId('chartView', props)}
            />
          </Show>
          <Show when={chartVariant === 'priceDistribution'}>
            {/* Absolutely fills the (bounded) chart area so the chart scales with it
                instead of growing the card to its own content height. */}
            <Box position="absolute" top={0} left={0} right={0} bottom={0}>
              <PriceDistributionChart
                priceReport={props.priceReport}
                cars={props.data.filter((car) => !car.isMyVehicle)}
                myPrice={props.priceReport.sellingPrice.recommended.value}
                isLoading={props.isLoading}
                fillHeight
                isTitleHidden
                markerVariant="recommendedPrice"
                data-testid={suffixTestId('priceDistributionChart', props)}
              />
            </Box>
          </Show>
          <Show when={chartVariant === 'mapView'}>
            <MapView
              priceReport={props.priceReport}
              data={props.data}
              selectedVehicleIds={selectedVehicleIds}
              setSelectedVehicleIds={setSelectedVehicleIds}
              data-testid={suffixTestId('mapView', props)}
            />
          </Show>
          {/* The distribution chart owns its loading state (DataStatus spinner) —
              overlaying the generic spinner would show two spinners at once */}
          <Show when={props.isLoading && chartVariant !== 'priceDistribution'}>
            <Box position="absolute" top={0} left={25} right={0} bottom={20}>
              <VStack height="100%" align="center" justify="center">
                <Spinner data-testid={suffixTestId('loading', props)} />
              </VStack>
            </Box>
          </Show>
          <Show when={isChartToolbarVisible} whenFeatureEnabled={featureFlags.PR_CHART_ICONS_V2}>
            {isNotNil(chartHighlightEncodingStyle) ? (
              <ChartEncodingLegend
                encodingStyle={chartHighlightEncodingStyle}
                data-testid={suffixTestId('encodingLegend', props)}
              />
            ) : null}
          </Show>
        </Box>
        {selectedVehicleIds?.map((selectedVehicleId, index) => (
          <SelectedVehicleCard
            key={selectedVehicleId}
            vehicleId={selectedVehicleId}
            vehicle={props.data.find((vehicle) => vehicle.ad_id === selectedVehicleId)}
            priceReport={props.priceReport}
            catalogue={props.catalogue}
            shouldScrollIntoView={index === 0}
          />
        ))}
      </VStack>
    </ChartToolbarProvider>
  );
}
